// Ein Eintrag der Zeitleiste (ST-ACT-001, Slice 5a): Punkt mit Symbol, Art
// fett, Autor und Zeit, Text, darunter die eigene Wiedervorlage mit ihren
// Aktionen. Aus verlauf.tsx herausgezogen; das Verhalten der Knoepfe ist
// unveraendert, die Namen bleiben (Proben in tastatur/regelwerk haengen daran).

import type { ReactElement, ReactNode } from 'react';
import { Body1, Button, Caption1, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, mergeClasses } from '@fluentui/react-components';

import { dueLabel, followUpButtonName } from '../../tasks.ts';
import { authorLabel, interactionTypeLabel, sourceLabel, type InteractionView } from '../../interactions.ts';
import { formatInstant } from '../../parties.ts';
import { MehrKnopf } from '../../shell/mehr-knopf.tsx';
import { GanzeNotiz, useGekuerzt } from './ganze-notiz.tsx';
import { useKontaktStyles } from './stile.ts';

/**
 * Wie viele ANDERE sich an dieser Notiz etwas vorgemerkt haben. Der Server
 * zaehlt alle offenen im eigenen Kontext, die eigene eingeschlossen -- hier
 * wird sie abgezogen, damit "noch jemand" wirklich jemand anderes ist.
 */
export function fremdeVormerkungen(eintrag: InteractionView): number {
  const eigene = eintrag.myOpenFollowUp === undefined ? 0 : 1;
  return Math.max(0, eintrag.openFollowUpCount - eigene);
}

function fremdeVormerkungenSatz(anzahl: number): string {
  return `Auch bei ${anzahl} anderen vorgemerkt`;
}

/** Art und Zeitpunkt der Notiz, auf Minuten genau: "Notiz vom 10.09.2026, 16:00". */
export function notizBezugVon(eintrag: InteractionView): string {
  return `${interactionTypeLabel(eintrag.interactionType)} vom ${formatInstant(eintrag.occurredAt)}`;
}

/** Der Eintrag selbst: dorthin geht der Fokus nach "Ganzen Verlauf anzeigen". */
export const eintragId = (interactionId: string): string => `verlauf-eintrag-${interactionId}`;

/** Der Ausloeser von "Verschieben" bzw. "Wiedervorlage setzen": dorthin kehrt der Fokus nach "Abbrechen" zurueck. */
export const eintragAusloeserId = (interactionId: string): string => `verlauf-ausloeser-${interactionId}`;

function MehrSymbol(): ReactElement {
  return (
    <svg {...symbol}>
      <path d="M6 12h.01M12 12h.01M18 12h.01" strokeWidth={3} />
    </svg>
  );
}

const symbol = { viewBox: '0 0 24 24', width: 16, height: 16, 'aria-hidden': true, focusable: false, fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

function NotizSymbol(): ReactElement {
  return (
    <svg {...symbol}>
      <path d="M5 4h10l4 4v12H5zM15 4v4h4M8 12h8M8 16h6" />
    </svg>
  );
}
function UhrSymbol(): ReactElement {
  return (
    <svg {...symbol}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" />
    </svg>
  );
}
function HakenSymbol(): ReactElement {
  return (
    <svg {...symbol}>
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}
function WarnSymbol(): ReactElement {
  return (
    <svg {...symbol}>
      <path d="M12 4 3 20h18zM12 10v4.5M12 17.5v.01" />
    </svg>
  );
}

export function VerlaufEintrag({
  eintrag,
  letzter,
  busy,
  erledigt,
  formular,
  onSetzen,
  onAbhaken,
  onVerschieben,
  nurLesen = false,
  titelDoppelt = false,
  stelleUnterGleichen,
  kurz = false,
  stelleGleicherBezug,
}: {
  readonly eintrag: InteractionView;
  readonly letzter: boolean;
  readonly busy: boolean;
  /** In dieser Sitzung erledigt: der Eintrag zeigt es, ohne dass die Seite springt. */
  readonly erledigt: boolean;
  /** Das offene Vormerkformular dieses Eintrags, sonst nichts. */
  readonly formular: ReactNode;
  readonly onSetzen: () => void;
  readonly onAbhaken: (art: 'erledigt' | 'zurueckgezogen') => void;
  readonly onVerschieben: () => void;
  /** Der Kontakt ist nicht beschreibbar: nichts Neues vormerken oder verschieben (Server: 409). */
  readonly nurLesen?: boolean;
  /** Ein anderer sichtbarer Eintrag traegt eine Wiedervorlage mit demselben Titel. */
  readonly titelDoppelt?: boolean;
  /** Hiessen die Knoepfe auch mit Notizbezug gleich (gleiche Minute), die Stelle unter ihnen: „1. von 2“. */
  readonly stelleUnterGleichen?: string | undefined;
  /** Im Ueberblick: der Text hoechstens drei Zeilen, der volle im Register "Verlauf" und vorgelesen. */
  readonly kurz?: boolean;
  /** Dieselbe Art in derselben Minute: die Stelle unter ihnen, für den Namen von "Ganze Notiz". */
  readonly stelleGleicherBezug?: string | undefined;
}): ReactElement {
  const own = useKontaktStyles();
  const eigene = eintrag.myOpenFollowUp;
  const fremd = fremdeVormerkungen(eintrag);
  // Gleichnamige Knoepfe mehrerer Eintraege unterscheiden sich im Namen (Titel
  // der Wiedervorlage, bei "setzen" Art und Zeitpunkt der Notiz; WCAG 2.4.6);
  // der Notiztext bleibt Beschreibung und nicht Teil des Namens.
  const textId = `verlauf-text-${eintrag.interactionId}`;
  const notizBezug = notizBezugVon(eintrag);
  // Traegt der Name den Bezug noch nicht (eigener Titel), und steht derselbe Titel
  // an einem zweiten Eintrag, kommt er dazu: „Erledigt: Rückruf, Notiz vom …“.
  // Gleicher Titel UND gleiche Minute: dann zaehlt die Stelle im Verlauf („1. von 2“).
  const eindeutig = (name: string): string => {
    const mitBezug = titelDoppelt && !name.includes(notizBezug) ? `${name}, ${notizBezug}` : name;
    return stelleUnterGleichen === undefined ? mitBezug : `${mitBezug}, ${stelleUnterGleichen}`;
  };
  const knopfname = (wort: string): string | undefined =>
    eigene === undefined ? undefined : eindeutig(followUpButtonName(wort, eigene.title, notizBezug));
  const Punkt = eigene !== undefined ? UhrSymbol : erledigt ? HakenSymbol : NotizSymbol;
  // Nur wenn der Überblick den Text wirklich kürzt, gibt es "Ganze Notiz".
  const gekuerzt = useGekuerzt(textId, kurz);
  const meta = `${authorLabel(eintrag.authoredByMe, eintrag.interactionType)} · ${formatInstant(eintrag.occurredAt)} · ${sourceLabel(eintrag.sourceSystem)}`;

  return (
    <li id={eintragId(eintrag.interactionId)} tabIndex={-1} className={mergeClasses(own.zeitEintrag, !letzter && own.zeitLinie)}>
      <span className={mergeClasses(own.punkt, eigene !== undefined && own.punktOffen, erledigt && own.punktErledigt)} aria-hidden="true">
        <Punkt />
      </span>
      <div className={own.zeitKopf}>
        <Body1>
          <strong className={own.art}>{interactionTypeLabel(eintrag.interactionType)}</strong>
        </Body1>
        {/* Autor, Zeit und Quelle in einem stillen Satz; die Quelle steht an JEDEM
            Eintrag, auch "von Hand" (5.2 Nr. 5). */}
        <Caption1 className={own.wann}>
          {meta}
        </Caption1>
      </div>
      <Body1 className={mergeClasses(own.text, kurz && own.textKurz)} id={textId}>
        {eintrag.summary}
      </Body1>
      {gekuerzt ? <GanzeNotiz bezug={notizBezug} meta={meta} text={eintrag.summary} knopfId={`ganze-notiz-${eintrag.interactionId}`} notiz={eintrag.interactionType === 'note'} stelle={stelleGleicherBezug} /> : undefined}

      {/* DIE WIEDERVORLAGE (ADR-0052). Drei Lagen, und jede zeigt genau das,
          was jetzt moeglich ist (5.2.9): eine eigene offene mit ihren
          Aktionen, das Formular zum Setzen oder Verschieben, oder der Weg
          dorthin. Die Hauptaktion des Bildschirms bleibt "Notiz festhalten". */}
      {formular !== undefined && formular !== false && formular !== null ? (
        formular
      ) : (
        <>
          {/* v4 tl: an der offenen Wiedervorlage steht nur "Erledigt"; was seltener
              ist, liegt im Menue "…" (5.2 Nr. 3, progressive Offenlegung). Was
              Kolleginnen und Kollegen sich hier vorgemerkt haben, ist eine ZAHL und
              kein Name (ADR-0051 Ziffer 7); sie steht still in derselben Zeile. */}
          {eigene !== undefined ? (
            <div className={own.faelligZeile}>
              {eigene.overdue ? <WarnSymbol /> : <UhrSymbol />}
              <Caption1 className={mergeClasses(own.faelligText, eigene.overdue && own.ueberfaellig)}>
                {eigene.title} — {dueLabel(eigene.dueOn, eigene.overdue)}
              </Caption1>
              <Button appearance="secondary" size="small" className={own.eintragKnopf} disabled={busy} aria-label={knopfname('Erledigt')} aria-describedby={textId} icon={<HakenSymbol />} onClick={() => onAbhaken('erledigt')}>
                Erledigt
              </Button>
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <MehrKnopf
                    id={eintragAusloeserId(eintrag.interactionId)}
                    appearance="subtle"
                    size="small"
                    className={own.eintragMenue}
                    disabled={busy}
                    bezug={knopfname('Weitere Aktionen')}
                    icon={<MehrSymbol />}
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem aria-label={knopfname('Nicht mehr nötig')} onClick={() => onAbhaken('zurueckgezogen')}>
                      Nicht mehr nötig
                    </MenuItem>
                    {nurLesen ? undefined : (
                      <MenuItem aria-label={knopfname('Verschieben')} onClick={onVerschieben}>
                        Verschieben
                      </MenuItem>
                    )}
                  </MenuList>
                </MenuPopover>
              </Menu>
              {fremd > 0 ? <Caption1 className={own.wann}>{fremdeVormerkungenSatz(fremd)}</Caption1> : undefined}
            </div>
          ) : (
            <div className={own.vormerkung}>
              {erledigt ? <Caption1>erledigt</Caption1> : undefined}
              {nurLesen ? undefined : (
                <Button
                  id={eintragAusloeserId(eintrag.interactionId)}
                  appearance="subtle"
                  size="small"
                  className={own.eintragKnopf}
                  disabled={busy}
                  aria-label={`Wiedervorlage setzen: ${notizBezug}`}
                  aria-describedby={textId}
                  icon={<UhrSymbol />}
                  onClick={onSetzen}
                >
                  Wiedervorlage setzen
                </Button>
              )}
              {fremd > 0 ? <Caption1 className={own.wann}>{fremdeVormerkungenSatz(fremd)}</Caption1> : undefined}
            </div>
          )}
        </>
      )}
    </li>
  );
}
